import { useState } from 'react';
import { useMutation } from 'react-query';
import { Alert, Button, Checkbox, FormControlLabel, Stack, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { v4 as uuid } from 'uuid';
import type { HotelReservation, HotelExternalCancellationInput } from '@bandido/shared/types';
import { hotelReservationTransport } from './hotelReservationTransport';

export function HotelExternalCancellationPanel({
  reservation,
  disabled,
  onUpdated,
}: {
  reservation: HotelReservation;
  disabled: boolean;
  onUpdated: (value: HotelReservation) => void;
}) {
  const { t } = useTranslation();
  const key = 'hotelReservation.externalCancellation';
  const [open, setOpen] = useState(false);
  const [reference, setReference] = useState(reservation.hotelSnapshot.supplierReference ?? '');
  const [reason, setReason] = useState('');
  const [date, setDate] = useState('');
  const [accepted, setAccepted] = useState(false);
  const [submitted, setSubmitted] = useState<HotelExternalCancellationInput>();
  const save = useMutation(
    (input: HotelExternalCancellationInput) =>
      hotelReservationTransport.recordExternalCancellation(reservation.id, input),
    {
      retry: false,
      onSuccess: onUpdated,
      onError: (error: any, input) => {
        // A plain HTTP conflict can occur after the provider committed. Only this
        // request-scoped acknowledgment proves its durable intent was released.
        const rejection = error?.response?.data?.message;
        if (rejection?.code === 'external_cancellation_rejected' && rejection.requestId === input.requestId) {
          setSubmitted(undefined);
          setAccepted(false);
        }
      },
    },
  );
  const locked = disabled || save.isLoading;
  const validDate = Number.isFinite(Date.parse(date)) && Date.parse(date) <= Date.now();
  return (
    <Stack spacing={2} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
      <Button disabled={locked} onClick={() => setOpen(!open)}>
        {t(`${key}Title`)}
      </Button>
      {open && (
        <>
          <Typography variant="subtitle1">{t(`${key}Title`)}</Typography>
          <Alert severity="warning">{t(`${key}Help`)}</Alert>
          <TextField
            label={t('hotelReservation.reconciliationReference')}
            value={reference}
            disabled={locked || Boolean(submitted)}
            inputProps={{ maxLength: 200 }}
            onChange={(e) => setReference(e.target.value)}
          />
          <TextField
            label={t('hotelReservation.reconciliationReason')}
            value={reason}
            disabled={locked || Boolean(submitted)}
            inputProps={{ maxLength: 500 }}
            onChange={(e) => setReason(e.target.value)}
          />
          <TextField
            type="datetime-local"
            label={t(`${key}Date`)}
            InputLabelProps={{ shrink: true }}
            value={date}
            disabled={locked || Boolean(submitted)}
            onChange={(e) => setDate(e.target.value)}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={accepted}
                disabled={locked || Boolean(submitted)}
                onChange={(_, value) => setAccepted(value)}
              />
            }
            label={t(`${key}Accept`)}
          />
          {save.isError && <Alert severity="warning">{t(`${key}Error`)}</Alert>}
          <Button
            variant="contained"
            disabled={locked || !accepted || !reference.trim() || reason.trim().length < 5 || !validDate}
            onClick={() => {
              const input = submitted ?? {
                requestId: uuid(),
                supplierReference: reference.trim(),
                reason: reason.trim(),
                cancelledAt: new Date(date).toISOString(),
                confirmedExternalCancellation: true as const,
              };
              setSubmitted(input);
              save.mutate(input);
            }}
          >
            {t(`${key}Save`)}
          </Button>
        </>
      )}
    </Stack>
  );
}
